import { useEffect, useState } from 'react'
import { COLORS } from '../lib/theme'
import type { HuntDog } from '../lib/types'
import { useTranslation } from '../i18n'
import { Icon } from './Icon'
import { PlanDogAvatar } from './PlanDogAvatar'

export interface AddDogsModalProps {
  /** The signed-in user's own dogs — the only dogs a participant may add. */
  dogs: HuntDog[]
  /** The dogs of theirs already on the plan, pre-ticked when the sheet opens. */
  initiallySelectedIds: string[]
  /** Commit the chosen set and close. The host diffs it against what's registered. */
  onDone: (selectedIds: string[]) => void
  /** Cancel without changing anything. */
  onClose: () => void
  /** A register/unregister is in flight — Done shows its saving label and disables. */
  saving: boolean
}

/**
 * The add-dogs picker (CLUB-883): a checkbox row per the user's own dog
 * (avatar + name), a Done that hands the selection back, and a close that
 * cancels. The wide-screen form of the mobile bottom sheet. Presentation only —
 * it fetches nothing and knows nothing about the write; the clubhouse container
 * supplies the dogs and commits the result.
 */
export function AddDogsModal({ dogs, initiallySelectedIds, onDone, onClose, saving }: AddDogsModalProps) {
  const { t } = useTranslation('huntInvitation')
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initiallySelectedIds))

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('dogs.modalTitle')}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>
              {t('dogs.modalTitle')}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.outline }}>
              {t('dogs.modalSubtitle')}
            </p>
          </div>
          <button className="wh-btn-icon" onClick={onClose} title={t('close')} aria-label={t('close')}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '16px 0', overflowY: 'auto' }}>
          {dogs.map((dog) => {
            const checked = selected.has(dog.id)
            return (
              <label
                key={dog.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  background: COLORS.surfaceContainerLowest,
                  border: `1px solid ${checked ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(dog.id)}
                  disabled={saving}
                />
                <PlanDogAvatar colorArgb={dog.colorArgb} size={32} />
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dog.name}
                </div>
              </label>
            )
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="wh-btn wh-btn-primary"
            onClick={() => onDone([...selected])}
            disabled={saving}
          >
            {saving ? t('composer.saving') : t('dogs.done')}
          </button>
        </div>
      </div>
    </div>
  )
}
